import { interval } from 'rxjs'
import { distinctUntilChanged, mergeMap, map } from 'rxjs/operators'

import makePromise from './make-promise'
import OutlookRest from './rest'
import { getOffice } from './office'
import { isDesktopMac } from './client'
import { addBreadcrumb, captureException } from '../sentry-config'

export const saveItem = (office = getOffice()) =>
  makePromise(office.context.mailbox.item.saveAsync, office.context.mailbox.item)()

export const getMessageIds = async (office = getOffice()) => {
  const ewsItemId = await saveItem(office)

  return {
    ewsItemId,
    restItemId: office.context.mailbox.convertToRestId(
      ewsItemId,
      office.MailboxEnums.RestVersion.v2_0
    ),
  }
}

const transformRecipient = ({ displayName, emailAddress }) => ({ displayName, emailAddress })

export default class OutlookItem {
  constructor(office) {
    this.office = office
    this.rest = new OutlookRest(office)

    this.interlocutorObservable = interval(1000).pipe(
      mergeMap(() => this.getMessageDetails()),
      distinctUntilChanged(
        (a, b) => a.emailAddress === b.emailAddress && a.internetMessageId === b.internetMessageId
      )
    )

    this.attendeesObservable = interval(1000).pipe(
      mergeMap(() => this.getAttendees()),
      distinctUntilChanged((a, b) => a.length === b.length)
    )

    this.recurrenceObservable = interval(1000).pipe(
      mergeMap(() => this.getRecurrence()),
      map(recurrence => recurrence !== null),
      distinctUntilChanged()
    )
  }

  restMessage = null

  get item() {
    return this.office.context.mailbox.item
  }

  hasItem() {
    return Boolean(this.item)
  }

  hasItemId() {
    return this.hasItem() && Boolean(this.item.itemId)
  }

  save() {
    return makePromise(this.item.saveAsync, this.item)()
  }

  async close() {
    return this.item.close()
  }

  async getMessageDetails() {
    const { emailAddress } = await this.getInterlocutorDetails()

    return { emailAddress, internetMessageId: this.getInternetMessageId() }
  }

  async getAttendees() {
    if (!this.item || !this.item.requiredAttendees || !this.item.optionalAttendees) {
      return []
    }

    const requiredAttendees = await makePromise(this.item.requiredAttendees?.getAsync, this.item)()
    const optionalAttendees = await makePromise(this.item.optionalAttendees?.getAsync, this.item)()

    return [
      ...requiredAttendees,
      ...optionalAttendees.map(attendee => ({ ...attendee, optional: true })),
    ]
  }

  async getOrganizer() {
    const organizer = await makePromise(this.item.organizer.getAsync, this.item)()

    return organizer
  }

  async getItemType() {
    return this.item.itemType
  }

  getInternetMessageId() {
    return (
      this.item && this.item.internetMessageId && this.item.internetMessageId.replace(/^.|.$/g, '')
    )
  }

  async getRecipients() {
    const [to, cc, bcc] = await Promise.all([this.getTo(), this.getCc(), this.getBcc()])

    return {
      to: to.map(transformRecipient),
      cc: cc.map(transformRecipient),
      bcc: bcc.map(transformRecipient),
    }
  }

  getCustomProperties() {
    return makePromise(this.item.loadCustomPropertiesAsync, this.item)()
  }

  getBody = (coercionType = 'html', options = {}) => {
    if (this.restMessage) {
      return this.restMessage.body
    }
    return makePromise(this.item.body.getAsync, this.item.body)(coercionType, options)
  }

  getBodyType() {
    return makePromise(this.item.body.getTypeAsync, this.item.body)()
  }

  async setBody(body) {
    const setBody = makePromise(this.item.body.setAsync, this.item.body)

    const coercionType = await this.getBodyType()
    return setBody(body, { coercionType })
  }

  async prependBody(value) {
    const prependBody = makePromise(this.item.body.prependAsync, this.item.body)

    const coercionType = await this.getBodyType()
    return prependBody(value, { coercionType })
  }

  async insertToBody(snippet) {
    const setBody = makePromise(this.item.body.setSelectedDataAsync, this.item.body)

    const coercionType = await this.getBodyType()
    return setBody(snippet, { coercionType })
  }

  async getSubject() {
    if (this.item.normalizedSubject) {
      return this.item.normalizedSubject
    }
    if (this.restMessage) {
      return this.restMessage.subject
    }
    return makePromise(this.item.subject.getAsync, this.item.subject)()
  }

  async getRecurrence() {
    if (!this.item || !this.item.recurrence) {
      return null
    }

    const recurrence = await makePromise(this.item.recurrence?.getAsync, this.item)()

    return recurrence
  }

  async getItemId() {
    try {
      const itemId = await makePromise(this.item.getItemIdAsync, this.item)()

      return itemId
    } catch (e) {
      if (e.name !== 'ItemNotSaved') {
        captureException(e)
      }
      return null
    }
  }

  async getLocation() {
    const location = await makePromise(this.item.location.getAsync, this.item)()

    return location
  }

  async getEndTime() {
    const end = await makePromise(this.item.end.getAsync, this.item)()

    return end
  }

  async getStartTime() {
    const start = await makePromise(this.item.start.getAsync, this.item)()

    return start
  }

  getFrom() {
    if (this.item && this.item.from && this.item.from.emailAddress) {
      return this.item.from
    }
    return this.office.context.mailbox.userProfile
  }

  async getRecipientsByType(type) {
    const recipients = this.item[type]

    if (Array.isArray(recipients)) {
      return recipients
    }

    if (recipients) {
      return makePromise(recipients.getAsync, recipients)()
    }

    return []
  }

  getTo() {
    return this.getRecipientsByType('to')
  }

  getCc() {
    return this.getRecipientsByType('cc')
  }

  getBcc() {
    return this.getRecipientsByType('bcc')
  }

  getAttachments() {
    if (this.restMessage) {
      return this.restMessage.attachments
    }
    return []
  }

  async getAppointmentAttachments() {
    const attachments = await makePromise(this.item.getAttachmentsAsync, this.item)()

    const attachmentsWithContent = await Promise.all(
      attachments.map(async ({ id, name, contentType }) => {
        const { content } = await makePromise(this.item.getAttachmentContentAsync, this.item)(id)

        return {
          title: name,
          mime_type: contentType || 'foo/bar', // temporary solution, because mime_tyle is required for gmail, but not for outlook
          file_url: 'https://microsoft.com',
          content: content,
        }
      })
    )

    return attachmentsWithContent
  }

  async getInterlocutorDetails() {
    if (!this.hasItem()) {
      return { emailAddress: null, displayName: null }
    }
    const itemFrom = this.getFrom()
    const [firstInterlocutor] = await this.getTo()

    if (itemFrom.emailAddress !== this.office.context.mailbox.userProfile.emailAddress) {
      return itemFrom
    } else if (firstInterlocutor) {
      return firstInterlocutor
    }

    return { emailAddress: null, displayName: null }
  }

  setSubject(subject) {
    return makePromise(this.item.subject.setAsync, this.item.subject)(subject)
  }

  setLocation(location) {
    if (location) {
      return makePromise(this.item.location.setAsync, this.item.location)(location)
    }
  }

  setSubjectInNewMessage(subject) {
    this.getSubject().then(result => {
      if (result.indexOf('Re: ') !== 0) {
        this.setSubject(subject)
      }
    })
  }

  setSubjectUnlessPresent(subject) {
    const { hostVersion } = this.office.context.mailbox.diagnostics
    const versionWithOldUI = '16'

    if (!this.item.conversationId) {
      this.setSubject(subject)
      return
    } else if (isDesktopMac() && hostVersion > versionWithOldUI) {
      this.setSubjectInNewMessage(subject)
    }
  }

  async getTemplateId() {
    try {
      const customProperties = await this.getCustomProperties()
      return customProperties.get('templateId') || null
    } catch (e) {
      return null
    }
  }

  async getCrmReferences() {
    try {
      const customProperties = await this.getCustomProperties()

      return customProperties.get('crmReferences') || null
    } catch (e) {
      return null
    }
  }

  async getMeetingTypeId() {
    try {
      const customProperties = await this.getCustomProperties()

      return customProperties.get('meetingTypeId') || null
    } catch (e) {
      return null
    }
  }

  async getCrmCustomFields() {
    try {
      const customProperties = await this.getCustomProperties()

      return customProperties.get('crmCustomFields') || {}
    } catch (e) {
      return {}
    }
  }

  async getIsLofting() {
    try {
      const customProperties = await this.getCustomProperties()

      return customProperties.get('isLoft') || false
    } catch (e) {
      return false
    }
  }

  async getMainGuest() {
    try {
      const customProperties = await this.getCustomProperties()

      return customProperties.get('mainGuest') || {}
    } catch (e) {
      return {}
    }
  }

  async getMessageIds() {
    const itemId = await this.save()

    const restVersion = this.office.MailboxEnums.RestVersion.v2_0
    const ewsItemId = this.office.context.mailbox.convertToEwsId(itemId, restVersion)
    const restItemId = this.office.context.mailbox.convertToRestId(itemId, restVersion)

    return { ewsItemId, restItemId }
  }

  async getMessage() {
    addBreadcrumb({ message: 'Item: getMessage', category: 'office-js' })
    const { ewsItemId, restItemId } = await this.getMessageIds()
    this.restMessage = await this.rest.getCurrentMessage(restItemId)

    const [[{ emailAddress }], subject, body, attachments, templateId] = await Promise.all([
      this.getTo(),
      this.getSubject(),
      this.getBody(),
      this.getAttachments(),
      this.getTemplateId(),
    ])

    return {
      ewsItemId,
      to: emailAddress,
      subject,
      body,
      attachments,
      templateId,
    }
  }
}
